import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Factory, ArrowRight } from 'lucide-react';
import siteImage from '../../assets/project-1.jpg';

const services = [
  {
    icon: <Home className="w-5 h-5 text-amber-500" />,
    title: 'Residential',
    description: 'Single Story Houses, Upstairs Story Houses, and House Renovation.',
    href: '/services#residential',
  },
  {
    icon: <Building2 className="w-5 h-5 text-amber-500" />,
    title: 'Commercial',
    description: 'Temple (Renovation / Building), School (Renovation / Building), and Shops (Building).',
    href: '/services#commercial',
  },
  {
    icon: <Factory className="w-5 h-5 text-amber-500" />,
    title: 'Industrial',
    description: 'Store.',
    href: '/services#industrial',
  },
];

export function BuildingBetterSection() {
  return (
    <section className="bg-[#F8F9FB] py-10 md:py-14 relative overflow-hidden">
      {/* Background decorative blob */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-50 rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />

      <div className="container-custom relative z-10">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ── Left Content ── */}
          <div>
            {/* Section label */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="flex items-center gap-2.5 mb-4"
            >
              <div className="w-8 h-px bg-amber-500" />
              <span className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.22em]">Our Services</span>
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-1"
            >
              Building it Better
            </motion.h2>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-black text-amber-500 leading-tight mb-4 relative inline-block"
            >
              in Concrete
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber-300 rounded-full" />
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              viewport={{ once: true }}
              className="text-[15px] text-slate-500 mb-8 leading-relaxed max-w-sm"
            >
              We pride ourselves on delivering structural integrity and aesthetic excellence. Our expertise
              ensures that every concrete and steel structure we build stands the test of time.
            </motion.p>

            {/* Service Cards */}
            <div className="grid grid-cols-2 gap-2">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-white border border-slate-100 rounded-lg p-1 shadow-sm hover:shadow-md hover:border-amber-200 transition-all duration-300"
                >
                  {/* Icon circle */}
                  <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center mb-1 group-hover:bg-amber-100 transition-colors">
                    {service.icon}
                  </div>
                  <h3 className="font-bold text-slate-800 text-[10px] mb-0.5">{service.title}</h3>
                  <Link
                    to={service.href}
                    className="inline-flex items-center gap-1 text-amber-500 text-[13px] font-black uppercase tracking-[0.15em] hover:gap-2 transition-all duration-300"
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── Right Content — Image ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Amber square decoration top-right */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-amber-500 rounded-xl z-0" />

            {/* Image container */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl z-10">
              <img
                src={siteImage}
                alt="Construction Site"
                className="w-full h-full object-cover"
                style={{ minHeight: '320px' }}
              />
            </div>

            {/* Stats badge — floating bottom-left outside image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-5 -left-5 bg-slate-900 px-5 py-4 rounded-2xl shadow-2xl z-20"
            >
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-amber-400 font-black text-2xl leading-none">500+</p>
                  <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest">Projects Done</p>
                </div>
              </div>
              <p className="text-white/80 text-[12px] mt-2 leading-relaxed">
                Built with <span className="text-amber-400 font-bold">Strength.</span><br />
                Designed to <span className="text-amber-400 font-bold">Last.</span>
              </p>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
